import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';
import { CATALOG_VERSION, catalogs, supportedLanguages, directionFor, translate } from '../i18n/catalog';

import { LanguageContext, languageStorageKey } from './LanguageContext';

function readChoice(tenantId) {
  try { return localStorage.getItem("smmclone-independent:v1:" + (languageStorageKey(tenantId))); } catch { return null; }
}
function allowedChoice(config, preferred) {
  const enabled = config.languages.filter(row => row.enabled === true).map(row => row.code);
  return enabled.includes(preferred) ? preferred : enabled.includes(config.default_code) ? config.default_code : 'en';
}
function validConfig(config) {
  return config && typeof config.tenant_id === 'string' && config.tenant_id && config.catalog_version === CATALOG_VERSION
    && Array.isArray(config.languages) && config.languages.length === supportedLanguages.length
    && new Set(config.languages.map(row => row.code)).size === supportedLanguages.length
    && config.languages.every(row => Object.prototype.hasOwnProperty.call(catalogs, row.code)
      && typeof row.name === 'string' && row.name.length > 0 && row.name.length <= 60
      && typeof row.enabled === 'boolean' && row.direction === directionFor(row.code))
    && config.languages.some(row => row.code === config.default_code && row.enabled);
}

export function translateWithOverrides(config, language, key, fallback, parameters = {}) {
  const catalog = catalogs[language] || catalogs.en;
  const overrides = config?.translations?.[language];
  // Only keys implemented by this customer UI can be replaced. Values remain
  // text, with the same named-parameter rules as the bundled catalog.
  if (Object.prototype.hasOwnProperty.call(catalog, key) && overrides &&
      Object.prototype.hasOwnProperty.call(overrides, key) && typeof overrides[key] === 'string' && overrides[key].length <= 2000) {
    return overrides[key].replace(/\{([a-zA-Z][a-zA-Z0-9_]*)\}/g, (whole, name) =>
      Object.prototype.hasOwnProperty.call(parameters, name) ? String(parameters[name]) : whole);
  }
  return translate(language, key, fallback, parameters);
}

export function LanguageProvider({ children }) {
  const { user, api } = useAuth();
  const { tenantId, tenant, subdomain, isLoading, isMainDomain } = useTenant();
  const { pathname } = useLocation();
  const expectedTenant = user?.tenant_id || tenantId || tenant?.id || '';
  const identity = `${expectedTenant}:${user?.id || ''}:${subdomain || window.location.hostname}`;
  const latestIdentity = useRef(identity);
  latestIdentity.current = identity;
  const pendingSave = useRef(null);
  const [state, setState] = useState({ identity: '', config: null, language: 'en', error: '' });
  const [version, setVersion] = useState(0);
  const signed = Boolean(user);
  const active = !isLoading && (signed || isMainDomain === false);
  const current = state.identity === identity ? state : null;
  const language = current?.language || 'en';
  const customerPage = !/^\/(sadmin|admin|owner|superadmin|super-admin|my)(\/|$)/.test(pathname);
  useEffect(() => {
    if (!active) return undefined;
    const controller = new AbortController();
    setState({ identity, config: null, language: 'en', error: '' });
    const request = signed ? api.get('/languages/customer/current', { signal: controller.signal })
      : fetch(`/api/public/languages/${encodeURIComponent(subdomain || window.location.hostname)}`, { signal: controller.signal })
        .then(async response => { if (!response.ok) throw new Error('Language settings unavailable'); return { data: await response.json() }; });
    request.then(({ data }) => {
      if (!validConfig(data) || (expectedTenant && data.tenant_id !== expectedTenant)
          || (signed && (data.user_id !== user.id || !Number.isSafeInteger(data.preference_revision) || data.preference_revision < 0))) throw new Error('Invalid language settings');
      if (!controller.signal.aborted) setState({ identity, config: data,
        language: allowedChoice(data, signed ? data.preferred_language : readChoice(data.tenant_id)), error: '', saving: false });
    }).catch(() => {
      if (!controller.signal.aborted) setState({ identity, config: null, language: 'en', error: 'language.unavailable' });
    });
    return () => {
      controller.abort();
      if (pendingSave.current?.identity === identity) pendingSave.current = null;
    };
  }, [active, api, identity, expectedTenant, subdomain, signed, user?.id, version]);
  useEffect(() => {
    const previous = { dir: document.documentElement.getAttribute('dir'), lang: document.documentElement.getAttribute('lang') };
    document.documentElement.setAttribute('lang', customerPage ? language : 'en');
    document.documentElement.setAttribute('dir', customerPage ? directionFor(language) : 'ltr');
    return () => {
      for (const key of ['dir', 'lang']) {
        if (previous[key] === null) document.documentElement.removeAttribute(key);
        else document.documentElement.setAttribute(key, previous[key]);
      }
    };
  }, [language, customerPage]);
  const setLanguage = useCallback(async code => {
    if (!current?.config || !current.config.languages.some(row => row.code === code && row.enabled)) return false;
    if (signed) {
      if (pendingSave.current?.identity === identity) return false;
      const request = { identity };
      pendingSave.current = request;
      setState(previous => previous.identity === identity ? { ...previous, saving: true, error: '' } : previous);
      try {
        const { data } = await api.put('/languages/customer/current', { preferred_language: code,
          preference_revision: current.config.preference_revision });
        if (!validConfig(data) || data.tenant_id !== expectedTenant || data.user_id !== user.id
            || data.preferred_language !== code || data.preference_revision !== current.config.preference_revision + 1) throw new Error('Unconfirmed language save');
        if (latestIdentity.current === identity && pendingSave.current === request) {
          setState({ identity, config: data, language: allowedChoice(data, data.preferred_language), saving: false, error: '' });
        }
        return true;
      } catch {
        if (latestIdentity.current === identity && pendingSave.current === request) {
          setState(previous => previous.identity === identity ? { ...previous, saving: false, error: 'language.saveFailed' } : previous);
        }
        return false;
      } finally {
        if (pendingSave.current === request) pendingSave.current = null;
      }
    }
    try { localStorage.setItem("smmclone-independent:v1:" + (languageStorageKey(current.config.tenant_id)), code); } catch { /* Selection still works without storage permission. */ }
    setState(previous => previous.identity === identity ? { ...previous, language: code } : previous);
    return true;
  }, [current, identity, signed, api, expectedTenant, user?.id]);
  const value = useMemo(() => ({ language, direction: directionFor(language),
    t: (key, fallback, params) => translateWithOverrides(current?.config, language, key, fallback, params),
    languages: current?.config?.languages.filter(row => row.enabled) || [],
    loading: active && !current?.config && !current?.error, error: current?.error || '',
    saving: current?.saving === true,
    canSelect: Boolean(current?.config), setLanguage, retry: () => setVersion(number => number + 1) }), [language, current, active, setLanguage]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
